import React, { useMemo, useState } from 'react';
import theme from './ProductThumbnails.module.scss';
import { classnames } from '@/utils/html';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Thumbnail } from '@ygtechnologies/bygr-frontend-component-library';
import { LightBox, RubusMedia } from '@ygtechnologies/rubus-ui';
import { ProductImagePlaceholder } from '@/components/ProductImagePlaceholder';
import { ProductSourceCatalog } from '@/types';
import {
    getProfileCatalogProductFileThumbnailUrl,
    getProfileCatalogProductFileUrl,
} from '@/services/profile-catalog-products';
import { getProjectCatalogProductFileThumbnailUrl, getProjectCatalogProductFileUrl } from '@/services/project-catalog';
export interface ImageLike {
    id: number;
    sortOrder: number;
}
interface ProductThumbnailsProps {
    images: ImageLike[] | null | undefined;
    thumbnailUrlGenerator(id: number): string;
    imageUrlGenerator(id: number): string;
    className?: string;
    onLightboxToggled?(open: boolean): void;
    disableOnClick?: boolean;
    size?: number;
    /** Hide the "+N" badge that counts additional images (used by the compact showroom card). */
    hideImageCount?: boolean;
}

export const ProductThumbnails: React.FC<ProductThumbnailsProps> = ({
    images,
    thumbnailUrlGenerator,
    imageUrlGenerator,
    className,
    onLightboxToggled,
    disableOnClick,
    size = 100,
    hideImageCount,
}) => {
    const [lightboxOpenIndex, setLightboxOpenIndex] = useState<number | null>(null);
    const safeImages = images ?? [];

    const handleThumbnailClick = (image: ImageLike) => {
        if (disableOnClick) return;
        var index = safeImages.indexOf(image);

        if (index > -1) {
            setLightboxOpenIndex(index);
            toggleLightbox(true);
        }
    };

    const toggleLightbox = (flag: boolean) => {
        triggerHandler(onLightboxToggled, flag);
    };

    const media = useMemo<RubusMedia[]>(
        () =>
            safeImages.map((x) => ({
                src: imageUrlGenerator(x.id),
                thumbnailSrc: imageUrlGenerator(x.id),
                type: 'image',
            })),
        [safeImages, imageUrlGenerator]
    );

    return (
        <>
            {safeImages.length > 0 && (
                <LightBox images={media} activeIndex={lightboxOpenIndex} setIndex={setLightboxOpenIndex} />
            )}

            {safeImages[0] ? (
                <div
                    style={{ '--size': size + 'px' } as any}
                    className={classnames(theme.thumbnail, className, { [theme.disableOnClick]: disableOnClick })}
                    onClick={(e) => handleThumbnailClick(safeImages[0])}>
                    <Thumbnail width={size} height={size} src={thumbnailUrlGenerator(safeImages[0].id)} />
                    {!hideImageCount && safeImages.length > 1 && (
                        <span className={theme.imageCount}>+{safeImages.length - 1}</span>
                    )}
                </div>
            ) : (
                <div className={classnames(theme.empty, className)} style={{ '--size': size + 'px' } as any}>
                    <ProductImagePlaceholder />
                </div>
            )}

            {/* {images.length > 1 && <div className={theme.additionalImageCount}>+{images.length - 1}</div>} */}

            {/* {images.length > 1 && (
                    <div className={theme.secondaryThumbnails}>
                        {images.slice(1).map(image =>
                            <div className={theme.thumbnail} key={image.id} onClick={e => this.handleThumbnailClick(image)}>
                                <Thumbnail src={thumbnailUrlGenerator(image.id)} />
                            </div>
                        )}
                    </div>
                )} */}
        </>
    );
};

export const CatalogProductThumbnails: React.FC<
    Omit<ProductThumbnailsProps, 'thumbnailUrlGenerator' | 'imageUrlGenerator'> & { catalog: ProductSourceCatalog }
> = ({ catalog, ...rest }) => {
    return (
        <ProductThumbnails
            imageUrlGenerator={
                catalog === ProductSourceCatalog.Profile ? getProfileCatalogProductFileUrl : getProjectCatalogProductFileUrl
            }
            thumbnailUrlGenerator={
                catalog === ProductSourceCatalog.Profile
                    ? getProfileCatalogProductFileThumbnailUrl
                    : getProjectCatalogProductFileThumbnailUrl
            }
            {...rest}
        />
    );
};
